import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/context/ToastContext";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import type {
  Task,
  TaskPriority,
  TaskRelatedType,
  TaskType,
} from "@/types/task";
import {
  PhoneCall,
  Mail,
  CalendarClock,
  CalendarCheck2,
  MessageSquare,
  CheckSquare2,
  Clock,
  UserCheck,
  Tag,
  AlignLeft,
  Link2,
} from "lucide-react";

interface AssignableUser {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role_name?: string;
}

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: Task | null;
  defaultRelated?: {
    type: TaskRelatedType;
    id: string;
    name?: string;
  };
  onSaved: (task: Task) => void;
}

export function TaskDialog({
  open,
  onOpenChange,
  task,
  defaultRelated,
  onSaved,
}: TaskDialogProps) {
  const { success, error: toastError } = useToast();
  const [loading, setLoading] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);

  // Form states
  const [title, setTitle] = useState("");
  const [taskType, setTaskType] = useState<TaskType>("FOLLOW_UP");
  const [priority, setPriority] = useState<TaskPriority>("NORMAL");
  const [dueAt, setDueAt] = useState("");
  const [description, setDescription] = useState("");
  const [assignedToId, setAssignedToId] = useState("");
  const [relatedType, setRelatedType] = useState<TaskRelatedType | "">("");
  const [relatedId, setRelatedId] = useState("");
  const [relatedName, setRelatedName] = useState("");

  // Load assignable users
  useEffect(() => {
    if (!open) return;
    api
      .get("/api/User/Assignable")
      .then((res) => {
        const users = unwrapArray<AssignableUser>(res);
        setAssignableUsers(users);
      })
      .catch((err) => {
        console.warn("Could not fetch assignable users:", err);
      });
  }, [open]);

  // Sync form when dialog opens or editing task changes
  useEffect(() => {
    if (!open) return;

    if (task) {
      setTitle(task.title || "");
      setTaskType(task.task_type || "FOLLOW_UP");
      setPriority(task.priority || "NORMAL");
      // Format to datetime-local string (YYYY-MM-DDTHH:mm)
      if (task.due_at) {
        const d = new Date(task.due_at);
        const pad = (n: number) => n.toString().padStart(2, "0");
        const dtLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
          d.getDate()
        )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setDueAt(dtLocal);
      } else {
        setDueAt("");
      }
      setDescription(task.description || "");
      setAssignedToId(task.assigned_to_id || "");
      setRelatedType(task.related_type || "");
      setRelatedId(task.related_id || "");
      setRelatedName(task.related_name || "");
    } else {
      // Default: 1 hour from now
      const defaultDue = new Date(Date.now() + 60 * 60 * 1000);
      const pad = (n: number) => n.toString().padStart(2, "0");
      const dtLocal = `${defaultDue.getFullYear()}-${pad(
        defaultDue.getMonth() + 1
      )}-${pad(defaultDue.getDate())}T${pad(defaultDue.getHours())}:${pad(
        defaultDue.getMinutes()
      )}`;

      setTitle(defaultRelated ? `Follow-up with ${defaultRelated.name || "contact"}` : "");
      setTaskType("FOLLOW_UP");
      setPriority("NORMAL");
      setDueAt(dtLocal);
      setDescription("");
      setAssignedToId("");

      if (defaultRelated) {
        setRelatedType(defaultRelated.type);
        setRelatedId(defaultRelated.id);
        setRelatedName(defaultRelated.name || "");
      } else {
        setRelatedType("");
        setRelatedId("");
        setRelatedName("");
      }
    }
  }, [open, task, defaultRelated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toastError("Validation Error", "Please provide a task title.");
      return;
    }
    if (!dueAt) {
      toastError("Validation Error", "Please select a due date and time.");
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        title: title.trim(),
        task_type: taskType,
        priority: priority,
        due_at: new Date(dueAt).toISOString(),
        description: description.trim() || undefined,
        assigned_to_id: assignedToId || undefined,
        related_type: relatedType || undefined,
        related_id: relatedId || undefined,
        related_name: relatedName || undefined,
      };

      let saved: Task;
      if (task) {
        const res = await api.put(`/api/Task/Update/${task.id}`, payload);
        saved = unwrapObject<Task>(res);
        success("Task Updated", "Task details have been saved.");
      } else {
        const res = await api.post("/api/Task/Insert", payload);
        saved = unwrapObject<Task>(res);
        success("Task Created", "New task scheduled successfully.");
      }

      onSaved(saved);
      onOpenChange(false);
    } catch (err: any) {
      toastError("Error", err.message || "Failed to save task.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl md:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare2 className="size-5 text-primary" />
            <span>{task ? "Edit Task" : "Schedule New Task"}</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-2">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="task-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Call regarding revised quotation"
              className="mt-1"
              required
            />
          </div>

          {/* Task Type & Priority */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Tag className="size-4 text-muted-foreground" />
                <span>Task Type</span>
              </Label>
              <Select
                value={taskType}
                onValueChange={(val: TaskType) => setTaskType(val)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CALL">
                    <span className="flex items-center gap-2">
                      <PhoneCall className="size-4 text-blue-600" />
                      <span>Phone Call</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="FOLLOW_UP">
                    <span className="flex items-center gap-2">
                      <CalendarCheck2 className="size-4 text-indigo-600" />
                      <span>Follow-up</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="MEETING">
                    <span className="flex items-center gap-2">
                      <CalendarClock className="size-4 text-purple-600" />
                      <span>Meeting</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="EMAIL">
                    <span className="flex items-center gap-2">
                      <Mail className="size-4 text-sky-600" />
                      <span>Email</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="WHATSAPP">
                    <span className="flex items-center gap-2">
                      <MessageSquare className="size-4 text-emerald-600" />
                      <span>WhatsApp</span>
                    </span>
                  </SelectItem>
                  <SelectItem value="OTHER">
                    <span className="flex items-center gap-2">
                      <CheckSquare2 className="size-4 text-slate-600" />
                      <span>Other</span>
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={priority}
                onValueChange={(val: TaskPriority) => setPriority(val)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HIGH">
                    <span className="text-red-600 font-semibold">High Priority</span>
                  </SelectItem>
                  <SelectItem value="NORMAL">
                    <span className="text-slate-800">Normal</span>
                  </SelectItem>
                  <SelectItem value="LOW">
                    <span className="text-muted-foreground">Low</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Due Date & Assignee */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="task-due-at" className="flex items-center gap-1.5">
                <Clock className="size-4 text-muted-foreground" />
                <span>Due Date & Time</span> <span className="text-destructive">*</span>
              </Label>
              <Input
                id="task-due-at"
                type="datetime-local"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className="mt-1"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <UserCheck className="size-4 text-muted-foreground" />
                <span>Assigned To</span>
              </Label>
              <Select
                value={assignedToId}
                onValueChange={(val) => setAssignedToId(val)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Myself (Default)" />
                </SelectTrigger>
                <SelectContent>
                  {assignableUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email}
                      {u.role_name ? ` (${u.role_name})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Linked Record (if not passed as defaultRelated) */}
          {defaultRelated ? (
            <div className="p-3 rounded-lg border border-border bg-muted/40 text-sm flex items-center gap-2.5">
              <Link2 className="size-4 text-muted-foreground shrink-0" />
              <span>
                Linked to <strong>{defaultRelated.type}</strong>: {defaultRelated.name || defaultRelated.id}
              </span>
            </div>
          ) : (
            <div className="p-3.5 rounded-lg border border-border bg-card space-y-2.5">
              <div className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-muted-foreground">
                <Link2 className="size-4" />
                <span>Related Record (Optional)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  value={relatedType}
                  onValueChange={(val: any) => setRelatedType(val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Related Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LEAD">Lead</SelectItem>
                    <SelectItem value="DEAL">Deal</SelectItem>
                    <SelectItem value="ACCOUNT">Account</SelectItem>
                    <SelectItem value="CONTACT">Contact</SelectItem>
                    <SelectItem value="QUOTE">Quote</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Record Name / Label"
                  value={relatedName}
                  onChange={(e) => setRelatedName(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="task-description" className="flex items-center gap-1.5">
              <AlignLeft className="size-4 text-muted-foreground" />
              <span>Notes / Agenda</span>
            </Label>
            <Textarea
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add extra context or instructions..."
              className="mt-1 resize-none"
              rows={3}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : task ? "Update Task" : "Create Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
